import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS, PI_SDK_CONFIG, GOOGLE_CONFIG } from '../constants/index.js';

/**
 * Authentication Service
 * Handles all authentication operations including Pi SDK integration,
 * Google Identity Services, token management, and API communication
 */
class AuthService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.authEndpoint = API_CONFIG.ENDPOINTS.AUTH;
    
    // Setup axios interceptors for automatic token attachment
    this.setupAxiosInterceptors();
  }

  /**
   * Setup axios interceptors to automatically attach tokens to requests
   */
  setupAxiosInterceptors() {
    // Configure axios to send and receive cookies
    axios.defaults.withCredentials = true;
    
    // Request interceptor - attach access token to all requests
    axios.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh on 401 errors
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshTokens();
            const token = this.getAccessToken();
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            this.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Authenticate user with Google Identity Service
   * @param {string} idToken - Google ID token (JWT) from GIS SDK
   * @returns {Promise<object>} Authentication response
   */
  async authenticateWithGoogle(idToken) {
    try {
      const response = await axios.post(`${this.baseURL}${this.authEndpoint}/google`, {
        idToken
      }, {
        withCredentials: true // Important for cookies
      });

      if (response.data.success) {
        // Store user data in sessionStorage
        this.storeUserData(response.data.data.user);
        
        return response.data;
      } else {
        throw new Error(response.data.error?.message || 'Authentication failed');
      }
    } catch (error) {
      console.error('Google authentication failed:', error);
      throw error;
    }
  }

  /**
   * Authenticate user with Pi Network
   * @param {string} piAccessToken - Access token from Pi SDK
   * @param {object} piUser - User data from Pi SDK
   * @returns {Promise<object>} Authentication response
   */
  async authenticateWithPi(piAccessToken, piUser) {
    try {
      const response = await axios.post(`${this.baseURL}${this.authEndpoint}/pi`, {
        accessToken: piAccessToken,
        user: piUser
      });

      if (response.data.success) {
        // Store tokens in sessionStorage
        const { tokens } = response.data.data;
        this.storeTokens(tokens.accessToken, tokens.refreshToken);
        
        // Store user data
        this.storeUserData(response.data.data.user);

        return response.data;
      } else {
        throw new Error(response.data.error?.message || 'Authentication failed');
      }
    } catch (error) {
      console.error('Authentication failed');
      const errorMessage = this.sanitizeErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  /**
   * Initiate Pi SDK authentication flow
   * @returns {Promise<object>} Authentication result
   */
  async loginWithPi() {
    try {
      // Check if Pi SDK is available
      if (!window.Pi) {
        throw new Error('Pi SDK not loaded. Please refresh the page.');
      }

      // Authenticate with Pi SDK
      const authResult = await window.Pi.authenticate(
        PI_SDK_CONFIG.SCOPES,
        this.onIncompletePaymentFound.bind(this)
      );

      // Send Pi authentication data to our backend
      const backendResponse = await this.authenticateWithPi(
        authResult.accessToken,
        authResult.user
      );

      return {
        success: true,
        user: backendResponse.data.user,
        profileStatus: backendResponse.data.profileStatus,
        message: backendResponse.message
      };
    } catch (error) {
      console.error('Pi login error:', error);
      throw new Error(error.message || 'Failed to authenticate with Pi Network');
    }
  }

  /**
   * Initiate Google Identity Service authentication flow
   * This method is now simplified - the actual authentication is handled
   * by the GoogleLoginButton component
   * @returns {Promise<object>} Authentication result
   */
  async loginWithGoogle() {
    return new Promise((resolve) => {
      //console.log('🔍 Google authentication flow initiated');
      //console.log('ℹ️ Authentication will be handled by Google button component');
      
      // The actual authentication is handled by the GoogleLoginButton component
      // which directly calls authenticateWithGoogle() with the credential
      resolve({
        success: true,
        message: 'Google authentication ready'
      });
    });
  }

  /**
   * Handle incomplete payments found during authentication
   * @param {object} payment - Incomplete payment data
   */
  onIncompletePaymentFound(payment) {
    console.warn('🔍 Incomplete payment found during authentication:', payment);
    
    // Store incomplete payment info for later handling
    if (payment.transaction && payment.transaction.verified && !payment.status.developer_completed) {
      sessionStorage.setItem('incomplete_payment', JSON.stringify({
        paymentId: payment.identifier,
        txid: payment.transaction.txid,
        amount: payment.amount,
        memo: payment.memo
      }));
    }
  }

  /**
   * Handle stored incomplete payment after successful login
   */
  async handleStoredIncompletePayment() {
    try {
      const storedPayment = sessionStorage.getItem('incomplete_payment');
      if (!storedPayment) return;

      const paymentData = JSON.parse(storedPayment);

      // Import donation service dynamically
      const { default: donationService } = await import('./donationService.js');
      
      const result = await donationService.completePayment(paymentData.paymentId, paymentData.txid);
      
      // Clear stored payment
      sessionStorage.removeItem('incomplete_payment');
      
      // Show success notification
      const toast = await import('react-hot-toast');
      toast.default.success(`Previous π${paymentData.amount} donation completed successfully! 🎉`);
      
      return result;
    } catch (error) {
      console.error('❌ Failed to complete stored incomplete payment:', error);
      
      // Clear stored payment even if completion failed
      sessionStorage.removeItem('incomplete_payment');
      
      // Show error notification
      const toast = await import('react-hot-toast');
      toast.default.error('Failed to complete previous donation. Please contact support.');
    }
  }

  /**
   * Refresh JWT tokens
   * Supports both sessionStorage (Pi auth) and httpOnly cookies (Google auth)
   * @returns {Promise<object>} New tokens
   */
  async refreshTokens() {
    try {
      const refreshToken = this.getRefreshToken();
      
      // For Google auth, refresh token is in httpOnly cookies (not accessible via JS)
      // For Pi auth, refresh token is in sessionStorage
      // We send the token in body if available, otherwise backend will use cookies
      const response = await axios.post(
        `${this.baseURL}${this.authEndpoint}/refresh`,
        {
          refreshToken: refreshToken || undefined
        },
        {
          withCredentials: true // Important: enables cookies to be sent/received
        }
      );

      if (response.data.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        // Only store in sessionStorage if we received tokens in response body (Pi auth)
        if (newRefreshToken) {
          this.storeTokens(accessToken, newRefreshToken);
        }
        // For Google auth, tokens are in httpOnly cookies set by backend
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      // Call backend logout endpoint
      await axios.post(`${this.baseURL}${this.authEndpoint}/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local tokens
      this.clearTokens();
    }
  }

  /**
   * Get current user data from backend
   * @returns {Promise<object>} User data
   */
  async getCurrentUser() {
    try {
      const response = await axios.get(`${this.baseURL}${this.authEndpoint}/me`);
      
      if (response.data.success) {
        this.storeUserData(response.data.data.user);
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch user data');
      }
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  /**
   * Store tokens in sessionStorage
   * @param {string} accessToken - JWT access token
   * @param {string} refreshToken - JWT refresh token
   */
  storeTokens(accessToken, refreshToken) {
    sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }

  /**
   * Store user data in sessionStorage
   * @param {object} userData - User data object
   */
  storeUserData(userData) {
    sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  }

  /**
   * Get access token from sessionStorage
   * @returns {string|null} Access token
   */
  getAccessToken() {
    return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token from sessionStorage
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    return sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get user data from sessionStorage
   * @returns {object|null} User data
   */
  getUserData() {
    const userData = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    try {
      // Get user data first as it's required for both auth methods
      const userData = this.getUserData();
      if (!userData) {
        return false;
      }

      // For Google auth, backend uses httpOnly cookies which JavaScript cannot read
      // For Pi auth, we store tokens in sessionStorage
      // So if user data exists, we consider the user authenticated
      // The backend will validate the httpOnly cookies or tokens when making API requests
      
      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      return false;
    }
  }

  /**
   * Clear all stored tokens and user data
   */
  clearTokens() {
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Check if Pi SDK is available
   * @returns {boolean} Pi SDK availability
   */
  isPiSDKAvailable() {
    return typeof window !== 'undefined' && !!window.Pi;
  }

  /**
   * Check if Google Identity Services SDK is available
   * @returns {boolean} GIS SDK availability
   */
  isGoogleSDKAvailable() {
    return typeof window !== 'undefined' && 
           !!window.google && 
           !!window.google.accounts && 
           !!window.google.accounts.id;
  }

  /**
   * Update user profile
   * @param {object} profileData - Profile data to update
   * @returns {Promise<object>} Updated user data
   */
  async updateProfile(profileData) {
    try {
      const response = await axios.put(`${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/profile`, profileData);
      
      if (response.data.success) {
        this.storeUserData(response.data.data.user);
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  /**
   * Get user profile by ID
   * @param {string} userId - User ID
   * @returns {Promise<object>} User profile data
   */
  async getUserProfile(userId) {
    try {
      const response = await axios.get(`${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/${userId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch user profile');
      }
    } catch (error) {
      console.error('Get user profile error:', error);
      throw error;
    }
  }

  /**
   * Get user statistics
   * @param {string} userId - User ID
   * @returns {Promise<object>} User statistics
   */
  async getUserStats(userId) {
    try {
      const response = await axios.get(`${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/${userId}/stats`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch user statistics');
      }
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error;
    }
  }

  /**
   * Check username availability
   * @param {string} username - Username to check
   * @returns {Promise<object>} Availability status
   */
  async checkUsernameAvailability(username) {
    try {
      const response = await axios.get(`${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/check-username/${username}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to check username availability');
      }
    } catch (error) {
      console.error('Check username availability error:', error);
      throw error;
    }
  }

  /**
   * Upload profile image
   * @param {File} imageFile - Image file to upload
   * @returns {Promise<object>} Upload result with image URLs
   */
  async uploadProfileImage(imageFile) {
    try {
      const formData = new FormData();
      formData.append('profileImage', imageFile);

      const response = await axios.post(
        `${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/upload-profile-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data.success) {
        this.storeUserData(response.data.data.user);
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to upload profile image');
      }
    } catch (error) {
      console.error('Upload profile image error:', error);
      throw error;
    }
  }

  /**
   * Get dashboard data (stats + projects in single request)
   * @returns {Promise<object>} Dashboard data including stats, projects, and recent activity
   */
  async getDashboardData() {
    try {
      const response = await axios.get(`${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/dashboard`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Get dashboard data error:', error);
      throw error;
    }
  }

  /**
   * Sanitize error messages to prevent sensitive data exposure
   * @param {Error} error - The error object
   * @returns {string} Sanitized error message
   */
  sanitizeErrorMessage(error) {
    const sensitiveKeywords = [
      'database', 'prisma', 'connection', 'pool', 'supabase',
      'aws', 'port', 'host', 'password', 'secret', 'key',
      'token', 'server', 'internal', 'stack', 'trace'
    ];

    let errorMessage = '';
    if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    const containsSensitiveInfo = sensitiveKeywords.some(keyword => 
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );

    if (containsSensitiveInfo) {
      if (error.response?.status === 401) {
        return 'Authentication failed. Please try again.';
      } else if (error.response?.status === 403) {
        return 'Access denied. Please check your permissions.';
      } else if (error.response?.status >= 500) {
        return 'Service temporarily unavailable. Please try again later.';
      } else {
        return 'An error occurred. Please try again.';
      }
    }

    return errorMessage || 'An unexpected error occurred. Please try again.';
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;